import React, { useState } from 'react';
import Card from '@/components/card';
import Title from '@/components/title';
import { settingMenu } from '@/configs/menuItems';
import SubMenu from '@/components/menu/sub-menu';
import { Button, notification, Space, Table } from 'antd';
import type { TableProps } from 'antd';
import { IDataModal } from '@/interfaces/dataModal';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import {
  PlusOutlined,
  EditFilled,
  DeleteFilled,
  UploadOutlined,
} from '@ant-design/icons';
import {
  deleteBusinessUnit,
  queryBusinessUnits,
  uploadBuLogo,
} from '@/adapters/business-unit';
import { IBusinessUnit } from '@/interfaces/business-unit';
import BusinessUnitModal from './components/business-unit-modal';
import DeleteConfirmModal from '@/components/modal/confirm-delete';
import { IUser } from '@/interfaces/user';
import UserTableModal from '@/components/user/user-table-modal';
import { getPathSegment } from '@/utils/pathUtils';
import { mediaUrl } from '@/utils/mediaUtils';
import MediaUploader from '@/components/media/uploader-modal';
import { TMediaType } from '@/interfaces/media';

const _modal: IDataModal<IBusinessUnit> = {
  open: false,
};

const _usersModal: IDataModal<IUser[]> = {
  open: false,
};

const _modalUpload: IDataModal<IBusinessUnit> = {
  open: false,
};

const BusinessUnit = () => {
  const pathSegment = getPathSegment();
  const [dataModal, setDataModal] = useState<IDataModal<IBusinessUnit>>(_modal);
  const [userModal, setUserModal] = useState<IDataModal<IUser[]>>(_usersModal);
  const [deleteModal, setDeleteModal] =
    useState<IDataModal<IBusinessUnit>>(_modal);

  const [updateModal, setUploadModal] = useState<IDataModal<IBusinessUnit>>({
    ..._modalUpload,
  });

  const { data, isLoading, refetch, isRefetching } = useQuery<
    AxiosResponse<IBusinessUnit[]>,
    Error
  >({
    queryKey: ['search-business-unit'],
    queryFn: async () => {
      const response = await queryBusinessUnits(); // Axios call returning a response object
      const businessUnits = response.data; // Extract the actual data array
      const enrichedData = await Promise.all(
        businessUnits.map(async unit => ({
          ...unit,
          imageUrl:
            (unit.mediaId && (await mediaUrl({ id: unit.mediaId || 0 }))) || '',
          signatureUrl:
            (unit.signatureMediaId &&
              (await mediaUrl({ id: unit.signatureMediaId || 0 }))) ||
            '',
        })),
      );
      response.data = enrichedData;
      return response;
    },
  });

  const onActionUnit = () => {
    const message = dataModal?.id ? 'อัพเดท BU สำเร็จ' : 'สร้าง BU สำเร็จ';
    notification.success({
      message: message,
    });
    setDataModal({ ..._modal });
    refetch();
  };

  const onDeleteUnit = async () => {
    await deleteBusinessUnit(Number(deleteModal?.id || 0));
    refetch();
    setDeleteModal({ ..._modal });
    notification.success({
      message: 'ลบ BU สำเร็จ',
    });
  };

  const columns: TableProps<IBusinessUnit>['columns'] = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 40,
      className: 'text-center',
      fixed: 'left',
      render: (_, rec, i) => <Space>{i + 1}</Space>,
    },
    {
      title: 'BU',
      dataIndex: 'name',
      key: 'name',
      className: 'font-bold',
    },
    {
      title: 'Image',
      dataIndex: 'imageUrl', // Assuming the id is the identifier for fetching the image
      key: 'imageUrl',
      render: (_, rec) => (
        <div className="flex flex-row gap-2 items-center">
          {(_ && (
            <img src={_} alt="Business Unit" style={{ height: '40px' }} />
          )) ||
            '*** upload Logo ***'}
          <div
            className="font-bold"
            onClick={() =>
              setUploadModal({
                open: true,
                item: rec,
                id: rec.id,
                optional: 'bu',
              })
            }
          >
            <UploadOutlined />
          </div>
        </div>
      ),
    },
    {
      title: 'Signature',
      dataIndex: 'signatureUrl', // Assuming the id is the identifier for fetching the image
      key: 'signatureUrl',
      render: (_, rec) => (
        <div className="flex flex-row gap-2 items-center">
          {(_ && (
            <img
              src={_}
              alt="Business Unit Signature"
              style={{ height: '40px' }}
            />
          )) ||
            '*** upload Signature ***'}
          <div
            className="font-bold"
            onClick={() =>
              setUploadModal({
                open: true,
                item: rec,
                id: rec.id,
                optional: 'signature',
              })
            }
          >
            <UploadOutlined />
          </div>
        </div>
      ),
    },
    {
      title: 'Auto Safety Email',
      dataIndex: 'isSafetyAutoSend',
      key: 'isSafetyAutoSend',
      align: 'center',
      className: 'font-bold',
      render: _ => (_ ? 'Auto' : ''),
    },
    {
      title: 'ผู้ใช้งาน',
      dataIndex: 'count',
      key: 'count',
      className: 'font-bold',
      width: 80,
      align: 'center',
      render: (_, rec) => (
        <Space
          className="cursor-pointer text-blue-700 underline"
          onClick={() =>
            setUserModal({
              open: true,
              id: rec.id,
              item: rec.users,
              optional: 'signature',
            })
          }
        >
          {rec.users?.length}
        </Space>
      ),
    },
    {
      dataIndex: 'action',
      key: 'action',
      width: 80,
      render: (_, rec) => (
        <div className="flex flex-row gap-4 items-center w-full justify-center">
          <DeleteFilled
            className="text-red-700"
            onClick={() =>
              setDeleteModal({
                open: true,
                item: rec,
                id: rec.id,
              })
            }
          />
          <EditFilled
            className="text-blue-500"
            onClick={() =>
              setDataModal({
                open: true,
                item: rec,
                id: rec.id,
              })
            }
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <SubMenu active={pathSegment} items={settingMenu} />
      <Card>
        <Title title="จัดการ Business Unit">
          <Button
            type="primary"
            className="max-w-[90px]"
            onClick={() => {
              setDataModal({
                ...dataModal,
                open: true,
              });
            }}
          >
            <PlusOutlined /> เพิ่มใหม่
          </Button>
        </Title>
        <div className="flex flex-col gap-3">
          <Table
            loading={isLoading || isRefetching}
            columns={columns}
            dataSource={data?.data || []}
            size="small"
            bordered
            className="font-ibm"
            pagination={false}
            scroll={{ x: 1550 }}
            rowKey={'businessUnitId'}
          />
          <BusinessUnitModal
            handleCancel={() => {
              setDataModal({ ..._modal });
            }}
            handleOk={() => {
              onActionUnit();
            }}
            dataModal={dataModal}
          />
          <DeleteConfirmModal
            content={`คุณต้องการลบ BU '${deleteModal?.item?.name}' ใช่หรือไม่`}
            handleCancel={() => {
              setDeleteModal({ ..._modal });
            }}
            handleOk={() => {
              onDeleteUnit();
            }}
            open={deleteModal?.open}
            title={`BU '${deleteModal?.item?.name}'`}
          />
          <UserTableModal
            data={userModal?.item || []}
            open={userModal?.open}
            handleCancel={() => {
              setUserModal({
                ..._usersModal,
              });
            }}
          />
          <MediaUploader
            data={{
              modelId: Number(updateModal?.id),
              modelType: updateModal.optional as TMediaType,
            }}
            open={updateModal.open}
            onCancel={() => {
              setUploadModal({
                ..._modalUpload,
              });
            }}
            onUploaded={() => {
              setUploadModal({
                ..._modalUpload,
              });
              refetch();
            }}
            ExternalApi={data => uploadBuLogo(data)}
          />
        </div>
      </Card>
    </>
  );
};

export default BusinessUnit;
